import {
  CHAT_UPLOAD_MAX_BYTES,
  MAX_CHAT_ATTACHMENTS_PER_MESSAGE,
  presignBliksyUploadContract,
  type BliksyChatAttachment,
  type PresignBliksyUploadRequest,
  type PresignBliksyUploadResponse
} from '@bliks-io/contracts';
import {
  CHAT_UPLOAD_ACCEPT,
  uploadToPresignedPost,
  useChatAttachmentDrafts,
  type ChatAttachmentDraftState,
  type UseChatAttachmentDraftsResult
} from '@bliks-io/ui';
import { apiClient } from '@/api/client';

/**
 * Why a file cannot be attached, as the key of the message that says so. The
 * frontend owns one translation namespace, so a reason code plus a lookup table
 * would only be indirection.
 */
export interface BliksyAttachmentRejection {
  key: string;
  values: { max?: number; maxMegabytes?: number };
}

export type BliksyAttachmentDraft = ChatAttachmentDraftState<BliksyAttachmentRejection>;

export interface UseBliksyAttachmentsResult extends Omit<
  UseChatAttachmentDraftsResult<BliksyAttachmentRejection>,
  'attachments'
> {
  /** The uploaded documents, keyed the way a Bliksy turn references them. */
  attachments: BliksyChatAttachment[];
  /** The picker filter — the same contract constant presign validates against. */
  accept: string;
}

const MAX_MEGABYTES = Math.floor(CHAT_UPLOAD_MAX_BYTES / (1024 * 1024));

const UPLOAD_FAILED: BliksyAttachmentRejection = {
  key: 'chat.attachment.uploadFailed',
  values: {}
};

const UNSUPPORTED_TYPE: BliksyAttachmentRejection = {
  key: 'chat.attachment.unsupported',
  values: {}
};

/**
 * This surface's own pre-checks, for SPEED and not for safety: refusing a 40 MB
 * file before a presign round-trip is the point. The type is not among them —
 * `useChatAttachmentDrafts` classifies it, since that answer is the same on
 * every chat surface. Every limit is enforced again at presign, pinned by the
 * presigned POST policy and re-checked against the real bytes at consume.
 */
function rejectBliksyAttachment(
  file: File,
  attachedCount: number
): BliksyAttachmentRejection | null {
  if (attachedCount >= MAX_CHAT_ATTACHMENTS_PER_MESSAGE) {
    return {
      key: 'chat.attachment.tooMany',
      values: { max: MAX_CHAT_ATTACHMENTS_PER_MESSAGE }
    };
  }
  if (file.size > CHAT_UPLOAD_MAX_BYTES) {
    return { key: 'chat.attachment.tooLarge', values: { maxMegabytes: MAX_MEGABYTES } };
  }
  return null;
}

/**
 * Owns the attachments of the Bliksy turn being composed.
 *
 * Bytes go DIRECTLY to object storage from the browser, so the chat request
 * carries storage keys only. Keys are minted per `(tenant, user, recording)` by
 * the Bliksy presign route, so a draft readied here is unusable from any other
 * chat surface — and `sessionKey` drops the drafts if the recording changes
 * under the panel, because such a key would hard-reject at send.
 */
export function useBliksyAttachments(sessionId: string): UseBliksyAttachmentsResult {
  const drafts = useChatAttachmentDrafts<BliksyAttachmentRejection>({
    reject: rejectBliksyAttachment,
    upload: async (file, { signal, onProgress, mediaType }) => {
      const request: PresignBliksyUploadRequest = {
        sessionId,
        filename: file.name,
        mediaType,
        // The EXACT byte length: the presigned policy pins it, so a rounded or
        // remembered size makes the object store refuse the body.
        size: file.size
      };
      const { data } = await apiClient.post<PresignBliksyUploadResponse>(
        presignBliksyUploadContract.path,
        request,
        { signal }
      );
      await uploadToPresignedPost(data.url, data.fields, file, { signal, onProgress });
      return { key: data.key };
    },
    uploadFailedRejection: UPLOAD_FAILED,
    unsupportedTypeRejection: UNSUPPORTED_TYPE,
    sessionKey: sessionId
  });

  return { ...drafts, accept: CHAT_UPLOAD_ACCEPT };
}
